const partnerTypes = [
  {
    icon: 'ri-code-s-slash-line',
    title: 'Technology Partners',
    description: 'Integrate your platform with Geofleet to offer seamless delivery management to your customers.',
    benefits: ['API & SDK access', 'Co-marketing opportunities', 'Dedicated integration support', 'Joint go-to-market'],
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Reseller Partners',
    description: 'Resell Geofleet solutions to your customers and earn recurring revenue on every deal.',
    benefits: ['Competitive margins', 'Sales enablement tools', 'Partner portal access', 'Deal registration'],
  },
  {
    icon: 'ri-links-line',
    title: 'Referral Partners',
    description: 'Refer businesses to Geofleet and earn commissions for every successful signup.',
    benefits: ['Generous commissions', 'Simple referral tracking', 'Marketing assets', 'No minimum commitments'],
  },
];

const benefits = [
  { icon: 'ri-money-dollar-circle-line', title: 'Revenue Growth', description: 'Earn commissions and margins on every customer you bring to Geofleet.' },
  { icon: 'ri-customer-service-2-line', title: 'Dedicated Support', description: 'Get a dedicated partner manager and priority technical support.' },
  { icon: 'ri-megaphone-line', title: 'Co-Marketing', description: 'Joint marketing campaigns, webinars, and content to drive mutual growth.' },
  { icon: 'ri-dashboard-3-line', title: 'Partner Portal', description: 'Track leads, deals, and commissions through our self-service partner portal.' },
  { icon: 'ri-graduation-cap-line', title: 'Training & Certification', description: 'Comprehensive training programs to become a Geofleet delivery expert.' },
  { icon: 'ri-global-line', title: 'Global Reach', description: 'Join a partner network spanning 20+ countries and growing.' },
];

import SEO, { breadcrumbJsonLd } from '../../components/SEO';
import { CLIENT_LOGO_NAMES } from '../../data/clientLogos';

export default function PartnersPage() {
  return (
    <div className="pt-24">
      <SEO
        title="Partner Program"
        description="Join the Geofleet partner ecosystem. Become a technology, reseller, or referral partner to expand your offerings, drive revenue, and deliver exceptional last-mile solutions."
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', url: 'https://geofleet.ai' },
          { name: 'Partners', url: 'https://geofleet.ai/partners' },
        ])}
      />
      {/* Hero */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-[#eef5ff] text-[#1769ff] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-team-line"></i>
                <span>Partner Program</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Grow together with{' '}
                <span className="text-[#1769ff]">Geofleet</span>
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed mb-8 max-w-lg">
                Join our partner ecosystem to expand your offerings, drive revenue, and deliver exceptional last-mile solutions to your customers.
              </p>
              <div className="flex flex-wrap gap-3">
                <a href="https://meetings.hubspot.com/aditya145" target="_blank" rel="noopener noreferrer" className="px-5 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-md shadow-[#1769ff]/15 cursor-pointer hover:opacity-90"
                style={{ backgroundColor: '#1769ff', color: 'white' }}>
                  Become a Partner
                </a>
                <a href="https://meetings.hubspot.com/aditya145" target="_blank" rel="noopener noreferrer" className="bg-white border border-gray-200 text-gray-700 px-5 py-2.5 rounded-lg text-sm font-medium hover:border-[#1769ff] hover:text-[#1769ff] transition-all cursor-pointer">
                  Learn More
                </a>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: '20+', label: 'Countries' },
                { value: '50+', label: 'Integrations' },
                { value: '5,000+', label: 'Supported IoT devices' },
                { value: '24/7', label: 'Partner portal access' },
              ].map((stat, i) => (
                <div key={i} className="bg-[#FAFAFA] rounded-2xl p-6 text-center border border-gray-100">
                  <div className="text-3xl font-bold text-[#1769ff] mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trusted by — same client names as home */}
      <section className="py-12 bg-[#FAFAFA] border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-xs text-gray-500 font-medium uppercase tracking-wider mb-8">
            Trusted by teams at leading enterprises
          </p>
          <div className="relative overflow-hidden">
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-[#FAFAFA] to-transparent z-10" />
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-[#FAFAFA] to-transparent z-10" />
            <div className="flex animate-slide-logos whitespace-nowrap">
              {[...CLIENT_LOGO_NAMES, ...CLIENT_LOGO_NAMES].map((name, i) => (
                <div key={i} className="flex-shrink-0 mx-8 text-sm font-bold text-gray-300 select-none">
                  {name}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Why partner with Geofleet?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Everything you need to succeed as a Geofleet partner</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-gray-100 hover:shadow-lg transition-all group">
                <div className="w-14 h-14 bg-[#eef5ff] rounded-2xl flex items-center justify-center mb-5 group-hover:bg-[#1769ff] transition-colors">
                  <i className={`${b.icon} text-2xl text-[#1769ff] group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{b.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Choose your path</h2>
            <p className="text-lg text-gray-600">Find the partnership model that works for your business</p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {partnerTypes.map((pt, i) => (
              <div key={i} className="rounded-2xl border border-gray-200 p-8 hover:border-[#1769ff] hover:shadow-xl transition-all">
                <div className="w-14 h-14 bg-[#eef5ff] rounded-2xl flex items-center justify-center mb-6">
                  <i className={`${pt.icon} text-2xl text-[#1769ff]`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{pt.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-6">{pt.description}</p>
                <ul className="space-y-2.5">
                  {pt.benefits.map((b, j) => (
                    <li key={j} className="flex items-center space-x-2.5 text-sm text-gray-700">
                      <i className="ri-check-line text-[#1769ff]"></i>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <a href="https://meetings.hubspot.com/aditya145" target="_blank" rel="noopener noreferrer" className="mt-8 block text-center bg-gray-100 text-gray-900 py-3 rounded-lg text-sm font-semibold hover:bg-[#1769ff] hover:text-white transition-all cursor-pointer">
                  Apply Now
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1769ff]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">Ready to partner with us?</h2>
          <p className="text-lg text-white/80 mb-8">Join 100+ partners already growing with Geofleet.</p>
          <a href="https://meetings.hubspot.com/aditya145" target="_blank" rel="noopener noreferrer" className="inline-block bg-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-all shadow-md cursor-pointer"
          style={{ color: '#1769ff' }}>
            Become a Partner
          </a>
        </div>
      </section>
    </div>
  );
}
